import React, { Fragment } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/footer/Footer";
import Scrollbar from "../../components/scrollbar/scrollbar";

const STEPS = [
  {
    title: "Visit GlobalGiftPass.io",
    desc: "Open www.globalgiftpass.io and click 'Redeem Voucher' in the navigation or on the homepage.",
  },
  {
    title: "Enter your voucher code",
    desc: "Type the unique code exactly as it appears in your confirmation email — codes are case-sensitive.",
  },
  {
    title: "Review package details",
    desc: "Your package details and value will be displayed. Review the inclusions and confirm everything looks right.",
  },
  {
    title: "Pick a date & time",
    desc: "Select your preferred date and time slot using the live booking calendar.",
  },
  {
    title: "Add your details",
    desc: "Enter your contact information, location (for at-home services), and any special preferences or requirements.",
  },
  {
    title: "Submit your booking",
    desc: "Send the redemption request. You'll receive a booking acknowledgement straight away.",
  },
  {
    title: "Concierge confirms",
    desc: "Our concierge team will contact you via WhatsApp or email within 24 hours to lock in all the final details.",
  },
  {
    title: "Enjoy the experience",
    desc: "Show up on the confirmed date and enjoy. We'll be available throughout if you need anything.",
  },
];

const CHANNELS = [
  {
    icon: (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
        <path d="M20 2H4a2 2 0 00-2 2v18l4-4h14a2 2 0 002-2V4a2 2 0 00-2-2z" />
      </svg>
    ),
    label: "Website Live Chat",
    value: "9:00 AM – 10:00 PM UAE time, 7 days a week",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
        <path d="M20.52 3.48A11.94 11.94 0 0012.06 0C5.5 0 .19 5.31.19 11.86c0 2.09.55 4.13 1.6 5.93L0 24l6.34-1.66a11.85 11.85 0 005.71 1.45h.01c6.55 0 11.86-5.31 11.86-11.86 0-3.17-1.23-6.15-3.4-8.45z" />
      </svg>
    ),
    label: "WhatsApp Support",
    value: "9:00 AM – 10:00 PM UAE time, 7 days a week",
    accent: "wa",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
        <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5z" />
      </svg>
    ),
    label: "Email Support",
    value: (
      <a href="mailto:support@globalgiftpass.io">support@globalgiftpass.io</a>
    ),
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
        <path d="M12 2L2 7v7c0 5.55 3.84 10.74 10 12 6.16-1.26 10-6.45 10-12V7z" />
      </svg>
    ),
    label: "VIP Concierge Line",
    value: "Dedicated 24/7 line for AED 3,000+ packages",
    accent: "vip",
  },
];

const BOOKING_TIERS = [
  {
    label: "Standard packages",
    notice: "Minimum 24 – 48 hours in advance",
    tone: "standard",
  },
  {
    label: "Premium experiences",
    notice: "Minimum 48 – 72 hours in advance",
    tone: "premium",
  },
  {
    label: "Luxury / VIP experiences",
    notice: "Minimum 7 days in advance",
    tone: "luxury",
  },
  {
    label: "International stays",
    notice: "Minimum 14 days in advance",
    tone: "international",
  },
];

const RESCHEDULE = [
  {
    icon: "check",
    text: "Changes made more than 48 hours before the experience are permitted, subject to your individual voucher terms and availability — contact the concierge to arrange.",
  },
  {
    icon: "x",
    text: "Rescheduling within 48 hours of the experience is not permitted and the voucher is forfeited in full.",
  },
  {
    icon: "info",
    text: "The rescheduled date must fall within the original voucher validity period.",
  },
  {
    icon: "x",
    text: "Promotional vouchers (AED 23 packages) cannot be rescheduled under any circumstances.",
  },
];

const SECURITY = [
  "Keep your voucher code confidential. GlobalGiftPass.io will honour the first redemption of any code in good faith.",
  "If your code is lost or stolen, contact support@globalgiftpass.io immediately. Code recovery is subject to identity verification.",
  "GlobalGiftPass.io cannot be held liable for vouchers redeemed by an unauthorised party if the code was shared by the holder.",
];

const HowToRedeem = () => {
  return (
    <Fragment>
      <Navbar />

      <section className="gphr-section">
        <div className="gphr-bg-orb gphr-bg-orb-tl" />
        <div className="gphr-bg-orb gphr-bg-orb-br" />

        <div className="gphr-inner">
          {/* HERO */}
          <div className="gphr-head">
            <div className="gphr-eyebrow">
              <span className="gphr-eyebrow-dot" />
              VOUCHER REDEMPTION
            </div>
            <h1 className="gphr-title">
              How to redeem your <span className="gphr-title-accent">voucher</span>
            </h1>
            <p className="gphr-sub">
              GlobalGiftPass operates a seamless online redemption system through
              our concierge platform. Redeem instantly after purchase or any
              time within the validity period.
            </p>
            <div className="gphr-hero-actions">
              <Link to="/redeem" className="gphr-btn gphr-btn-primary">
                Redeem Now
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </Link>
              <a
                href="https://wa.me/971545740174?text=Hello%20GlobalGiftPass%20concierge%2C%20I%20need%20help%20redeeming%20my%20voucher."
                target="_blank"
                rel="noreferrer"
                className="gphr-btn gphr-btn-wa"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20.52 3.48A11.94 11.94 0 0012.06 0C5.5 0 .19 5.31.19 11.86c0 2.09.55 4.13 1.6 5.93L0 24l6.34-1.66a11.85 11.85 0 005.71 1.45h.01c6.55 0 11.86-5.31 11.86-11.86 0-3.17-1.23-6.15-3.4-8.45z" /></svg>
                Chat on WhatsApp
              </a>
            </div>
          </div>

          {/* STEP-BY-STEP */}
          <div className="gphr-block">
            <div className="gphr-block-head">
              <div className="gphr-block-num">2.1</div>
              <h2>Step-by-step redemption</h2>
              <p>Follow these eight steps from your confirmation email to a confirmed booking.</p>
            </div>

            <div className="gphr-steps">
              {STEPS.map((s, i) => (
                <article className="gphr-step" key={i}>
                  <div className="gphr-step-num">
                    <span>{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <div className="gphr-step-body">
                    <h3>{s.title}</h3>
                    <p>{s.desc}</p>
                  </div>
                  {i < STEPS.length - 1 && <div className="gphr-step-line" />}
                </article>
              ))}
            </div>
          </div>

          {/* CONCIERGE SERVICE */}
          <div className="gphr-block">
            <div className="gphr-block-head">
              <div className="gphr-block-num">2.2</div>
              <h2>Concierge redemption service</h2>
              <p>
                Our live concierge sits inside the website. Click the chat
                widget on <strong>www.globalgiftpass.io</strong> and a dedicated
                agent will help with redemption, booking, scheduling and any
                special requests during operating hours.
              </p>
            </div>

            <div className="gphr-channels">
              {CHANNELS.map((c, i) => (
                <div className={`gphr-channel ${c.accent ? `gphr-channel-${c.accent}` : ""}`} key={i}>
                  <div className="gphr-channel-icon">{c.icon}</div>
                  <div className="gphr-channel-meta">
                    <span className="gphr-channel-label">{c.label}</span>
                    <span className="gphr-channel-value">{c.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ADVANCE BOOKING */}
          <div className="gphr-block">
            <div className="gphr-block-head">
              <div className="gphr-block-num">2.3</div>
              <h2>Advance booking requirements</h2>
              <p>
                Each package has a minimum advance booking period as specified
                in the individual package details. As a general guide:
              </p>
            </div>

            <div className="gphr-tier-grid">
              {BOOKING_TIERS.map((b, i) => (
                <div className={`gphr-tier gphr-tier-${b.tone}`} key={i}>
                  <div className="gphr-tier-icon">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                  </div>
                  <div className="gphr-tier-meta">
                    <span className="gphr-tier-label">{b.label}</span>
                    <span className="gphr-tier-notice">{b.notice}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CONFIRMATION + RESCHEDULE — 2 col */}
          <div className="gphr-split">
            <div className="gphr-block gphr-block-card">
              <div className="gphr-block-head gphr-block-head-tight">
                <div className="gphr-block-num">2.4</div>
                <h2>Booking confirmation</h2>
              </div>
              <div className="gphr-confirm">
                <div className="gphr-confirm-icon">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                </div>
                <p>
                  A booking is only confirmed once you receive a written
                  confirmation from GlobalGiftPass.io via email or WhatsApp.
                  Submitting a redemption request does not constitute a
                  confirmed booking.
                </p>
                <div className="gphr-confirm-warn">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M12 2L1 21h22L12 2zm-1 8h2v6h-2v-6zm0 8h2v2h-2v-2z" /></svg>
                  If you don't receive confirmation within 24 hours of your
                  request, contact{" "}
                  <a href="mailto:support@globalgiftpass.io">support@globalgiftpass.io</a>{" "}
                  immediately.
                </div>
              </div>
            </div>

            <div className="gphr-block gphr-block-card">
              <div className="gphr-block-head gphr-block-head-tight">
                <div className="gphr-block-num">2.5</div>
                <h2>Rescheduling</h2>
              </div>
              <ul className="gphr-rules">
                {RESCHEDULE.map((r, i) => (
                  <li key={i} className={`gphr-rule gphr-rule-${r.icon}`}>
                    <span className="gphr-rule-icon">
                      {r.icon === "check" ? (
                        <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>
                      ) : r.icon === "x" ? (
                        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                          <circle cx="12" cy="12" r="10" />
                          <text x="12" y="16" textAnchor="middle" fill="#0b0b0c" fontSize="12" fontWeight="700">i</text>
                        </svg>
                      )}
                    </span>
                    <span>{r.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* SECURITY */}
          <div className="gphr-block">
            <div className="gphr-block-head">
              <div className="gphr-block-num">2.6</div>
              <h2>Voucher code security</h2>
              <p>
                Treat your voucher code like cash. A few simple habits keep your
                redemption safe.
              </p>
            </div>

            <div className="gphr-security-card">
              <div className="gphr-security-icon">
                <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <ul className="gphr-security-list">
                {SECURITY.map((s, i) => (
                  <li key={i}>
                    <span className="gphr-bullet">
                      <svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>
                    </span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* FINAL CTA */}
          <div className="gphr-cta">
            <div className="gphr-cta-eyebrow">
              <span className="gphr-eyebrow-dot" />
              READY TO REDEEM?
            </div>
            <h3>Your experience is just <span>a code away</span></h3>
            <p>
              Have your voucher code handy, then jump straight into the
              redemption flow. Our concierge team is online if you need a hand.
            </p>
            <div className="gphr-cta-actions">
              <Link to="/redeem" className="gphr-btn gphr-btn-primary">
                Redeem Voucher
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </Link>
              <a href="mailto:support@globalgiftpass.io" className="gphr-btn gphr-btn-ghost">
                Email Support
              </a>
            </div>
          </div>
        </div>

        <style>{`
          .gphr-section {
            position: relative;
            background: linear-gradient(180deg, #07060a 0%, #0b0b0c 100%);
            color: #e6e8ec;
            padding: 60px 22px 90px;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            overflow: hidden;
          }
          .gphr-bg-orb {
            position: absolute;
            width: 460px; height: 460px;
            border-radius: 50%;
            filter: blur(140px);
            opacity: 0.4;
            pointer-events: none;
          }
          .gphr-bg-orb-tl { background: rgba(212,168,87,0.18); top: -180px; left: -120px; }
          .gphr-bg-orb-br { background: rgba(212,168,87,0.12); bottom: -200px; right: -120px; }

          .gphr-inner { position: relative; max-width: 1200px; margin: 0 auto; z-index: 1; }

          /* HERO */
          .gphr-head { text-align: center; margin-bottom: 48px; }
          .gphr-eyebrow {
            display: inline-flex; align-items: center; gap: 8px;
            padding: 6px 14px;
            background: rgba(212,168,87,0.1);
            border: 1px solid rgba(212,168,87,0.3);
            color: #d4a857;
            border-radius: 999px;
            font-size: 11px; font-weight: 700; letter-spacing: 2px;
            margin-bottom: 14px;
          }
          .gphr-eyebrow-dot {
            width: 6px; height: 6px; border-radius: 50%;
            background: #d4a857;
            box-shadow: 0 0 10px #d4a857;
            animation: gphr-pulse 1.6s ease-in-out infinite;
          }
          @keyframes gphr-pulse { 0%,100% { opacity:1; transform: scale(1); } 50% { opacity:.55; transform: scale(.85); } }
          .gphr-title {
            font-family: "Playfair Display", "Times New Roman", serif;
            font-size: 42px; font-weight: 700; color: #fff;
            margin: 0 0 14px; line-height: 1.15;
          }
          .gphr-title-accent {
            background: linear-gradient(135deg, #f1cf85, #b58a3f);
            -webkit-background-clip: text; background-clip: text; color: transparent;
            font-style: italic; font-weight: 600;
          }
          .gphr-sub { font-size: 15.5px; color: #9aa0a8; max-width: 720px; margin: 0 auto 22px; line-height: 1.65; }
          .gphr-hero-actions {
            display: inline-flex; flex-wrap: wrap; gap: 10px; justify-content: center;
          }

          /* BLOCK */
          .gphr-block { margin-bottom: 44px; }
          .gphr-block-head {
            text-align: center;
            max-width: 720px;
            margin: 0 auto 28px;
            position: relative;
          }
          .gphr-block-head-tight { margin-bottom: 18px; max-width: 100%; text-align: left; }
          .gphr-block-num {
            display: inline-block;
            font-family: "Playfair Display", serif;
            font-size: 13px;
            font-weight: 700;
            background: linear-gradient(135deg, #f1cf85, #b58a3f);
            -webkit-background-clip: text; background-clip: text; color: transparent;
            letter-spacing: 1.5px;
            margin-bottom: 6px;
          }
          .gphr-block-head h2 {
            font-family: "Playfair Display", serif;
            font-size: 26px; color: #fff; font-weight: 700;
            margin: 0 0 10px; line-height: 1.25;
          }
          .gphr-block-head p { font-size: 14px; color: #9aa0a8; margin: 0; line-height: 1.65; }
          .gphr-block-head p strong { color: #d4a857; }

          /* STEPS */
          .gphr-steps {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 14px;
          }
          .gphr-step {
            position: relative;
            display: grid;
            grid-template-columns: 56px 1fr;
            gap: 14px;
            padding: 20px;
            background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212,168,87,0.2);
            border-radius: 16px;
            transition: border-color .25s ease, transform .2s ease, box-shadow .25s ease;
          }
          .gphr-step:hover {
            border-color: rgba(212,168,87,0.55);
            transform: translateY(-2px);
            box-shadow: 0 12px 28px rgba(212,168,87,0.12);
          }
          .gphr-step-num {
            width: 56px; height: 56px;
            background: rgba(212,168,87,0.1);
            border: 1px solid rgba(212,168,87,0.32);
            border-radius: 14px;
            display: flex; align-items: center; justify-content: center;
            flex-shrink: 0;
          }
          .gphr-step-num span {
            font-family: "Playfair Display", serif;
            font-size: 22px;
            font-weight: 700;
            background: linear-gradient(135deg, #f1cf85, #b58a3f);
            -webkit-background-clip: text; background-clip: text; color: transparent;
          }
          .gphr-step-body { min-width: 0; }
          .gphr-step-body h3 {
            font-family: "Playfair Display", serif;
            font-size: 17px; color: #fff; font-weight: 700;
            margin: 0 0 6px; line-height: 1.3;
          }
          .gphr-step-body p { font-size: 13.5px; color: #9aa0a8; line-height: 1.6; margin: 0; }

          /* CHANNELS */
          .gphr-channels {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }
          .gphr-channel {
            display: grid;
            grid-template-columns: 44px 1fr;
            gap: 12px;
            align-items: center;
            padding: 18px 20px;
            background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212,168,87,0.22);
            border-radius: 14px;
            transition: border-color .25s ease, transform .2s ease;
          }
          .gphr-channel:hover {
            border-color: rgba(212,168,87,0.5);
            transform: translateY(-1px);
          }
          .gphr-channel-icon {
            width: 44px; height: 44px;
            background: rgba(212,168,87,0.12);
            border: 1px solid rgba(212,168,87,0.3);
            color: #d4a857;
            border-radius: 12px;
            display: flex; align-items: center; justify-content: center;
            flex-shrink: 0;
          }
          .gphr-channel-wa .gphr-channel-icon {
            background: linear-gradient(135deg, #25d366, #128c7e);
            border-color: transparent;
            color: #fff;
          }
          .gphr-channel-vip {
            border-color: rgba(212,168,87,0.45);
            background: linear-gradient(135deg, rgba(241,207,133,0.08), rgba(181,138,63,0.04));
          }
          .gphr-channel-vip .gphr-channel-icon {
            background: linear-gradient(135deg, #f1cf85, #b58a3f);
            color: #0b0b0c;
            border-color: transparent;
          }
          .gphr-channel-meta { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
          .gphr-channel-label {
            font-size: 13.5px; color: #fff; font-weight: 700;
            font-family: "Playfair Display", serif;
          }
          .gphr-channel-value { font-size: 12.5px; color: #9aa0a8; line-height: 1.5; }
          .gphr-channel-value a { color: #d4a857; text-decoration: none; font-weight: 600; }
          .gphr-channel-value a:hover { color: #f1cf85; text-decoration: underline; }

          /* TIER GRID */
          .gphr-tier-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 12px;
          }
          .gphr-tier {
            padding: 20px;
            background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212,168,87,0.22);
            border-radius: 14px;
            display: flex; flex-direction: column; gap: 12px;
            transition: border-color .25s ease, transform .2s ease;
          }
          .gphr-tier:hover { border-color: rgba(212,168,87,0.5); transform: translateY(-2px); }
          .gphr-tier-icon {
            width: 38px; height: 38px;
            background: rgba(212,168,87,0.1);
            border: 1px solid rgba(212,168,87,0.28);
            color: #d4a857;
            border-radius: 10px;
            display: flex; align-items: center; justify-content: center;
          }
          .gphr-tier-luxury .gphr-tier-icon,
          .gphr-tier-international .gphr-tier-icon {
            background: linear-gradient(135deg, #f1cf85, #b58a3f);
            color: #0b0b0c;
            border-color: transparent;
          }
          .gphr-tier-meta { display: flex; flex-direction: column; gap: 4px; }
          .gphr-tier-label {
            font-family: "Playfair Display", serif;
            font-size: 15px; color: #fff; font-weight: 700;
          }
          .gphr-tier-notice {
            font-size: 12.5px;
            color: #d4a857;
            font-weight: 600;
            letter-spacing: 0.3px;
          }

          /* SPLIT */
          .gphr-split {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            margin-bottom: 44px;
          }
          .gphr-block-card {
            background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212,168,87,0.22);
            border-radius: 18px;
            padding: 26px;
            margin-bottom: 0;
          }

          /* CONFIRM */
          .gphr-confirm { display: flex; flex-direction: column; gap: 14px; }
          .gphr-confirm-icon {
            width: 48px; height: 48px;
            background: rgba(74, 222, 128, 0.12);
            border: 1px solid rgba(74, 222, 128, 0.3);
            color: #4ade80;
            border-radius: 14px;
            display: flex; align-items: center; justify-content: center;
          }
          .gphr-confirm > p {
            font-size: 14px;
            color: #b8bcc4;
            line-height: 1.7;
            margin: 0;
          }
          .gphr-confirm-warn {
            display: flex; align-items: flex-start; gap: 10px;
            padding: 14px;
            background: rgba(255, 90, 90, 0.08);
            border: 1px solid rgba(255, 90, 90, 0.28);
            border-left: 3px solid #ff7a7a;
            border-radius: 10px;
            font-size: 13px;
            color: #c5c8cf;
            line-height: 1.55;
          }
          .gphr-confirm-warn svg { color: #ff7a7a; flex-shrink: 0; margin-top: 2px; }
          .gphr-confirm-warn a { color: #d4a857; text-decoration: none; font-weight: 700; }
          .gphr-confirm-warn a:hover { color: #f1cf85; text-decoration: underline; }

          /* RULES */
          .gphr-rules { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; }
          .gphr-rule {
            display: flex; align-items: flex-start; gap: 12px;
            padding: 12px 14px;
            background: rgba(255,255,255,0.02);
            border: 1px solid rgba(255,255,255,0.06);
            border-radius: 10px;
            font-size: 13.5px;
            color: #c5c8cf;
            line-height: 1.55;
          }
          .gphr-rule-icon {
            width: 22px; height: 22px;
            border-radius: 50%;
            display: inline-flex; align-items: center; justify-content: center;
            flex-shrink: 0;
            margin-top: 1px;
          }
          .gphr-rule-check .gphr-rule-icon {
            background: rgba(74, 222, 128, 0.14);
            border: 1px solid rgba(74, 222, 128, 0.32);
            color: #4ade80;
          }
          .gphr-rule-x .gphr-rule-icon {
            background: rgba(255, 90, 90, 0.14);
            border: 1px solid rgba(255, 90, 90, 0.32);
            color: #ff7a7a;
          }
          .gphr-rule-info .gphr-rule-icon {
            background: rgba(212,168,87,0.16);
            border: 1px solid rgba(212,168,87,0.32);
            color: #d4a857;
          }

          /* SECURITY */
          .gphr-security-card {
            display: grid;
            grid-template-columns: 56px 1fr;
            gap: 22px;
            align-items: flex-start;
            padding: 28px;
            background: linear-gradient(135deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212,168,87,0.3);
            border-radius: 18px;
          }
          .gphr-security-icon {
            width: 56px; height: 56px;
            background: linear-gradient(135deg, rgba(212,168,87,0.2), rgba(212,168,87,0.05));
            border: 1px solid rgba(212,168,87,0.4);
            color: #d4a857;
            border-radius: 14px;
            display: flex; align-items: center; justify-content: center;
          }
          .gphr-security-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 12px; }
          .gphr-security-list li {
            display: flex; align-items: flex-start; gap: 12px;
            font-size: 14px;
            color: #c5c8cf;
            line-height: 1.65;
          }
          .gphr-bullet {
            width: 20px; height: 20px;
            background: rgba(212,168,87,0.16);
            border: 1px solid rgba(212,168,87,0.32);
            color: #d4a857;
            border-radius: 50%;
            display: inline-flex; align-items: center; justify-content: center;
            flex-shrink: 0;
            margin-top: 2px;
          }

          /* CTA */
          .gphr-cta {
            position: relative;
            background: linear-gradient(135deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212,168,87,0.3);
            border-radius: 22px;
            padding: 50px 30px;
            text-align: center;
            overflow: hidden;
          }
          .gphr-cta::before {
            content: "";
            position: absolute;
            top: -100px; left: 50%;
            transform: translateX(-50%);
            width: 500px; height: 500px;
            background: radial-gradient(circle, rgba(212,168,87,0.18), transparent 70%);
            pointer-events: none;
          }
          .gphr-cta-eyebrow {
            position: relative;
            display: inline-flex; align-items: center; gap: 8px;
            padding: 6px 14px;
            background: rgba(212,168,87,0.1);
            border: 1px solid rgba(212,168,87,0.3);
            color: #d4a857;
            border-radius: 999px;
            font-size: 11px; font-weight: 700; letter-spacing: 2px;
            margin-bottom: 16px;
          }
          .gphr-cta h3 {
            position: relative;
            font-family: "Playfair Display", serif;
            font-size: 30px; color: #fff; margin: 0 0 10px; font-weight: 700; line-height: 1.25;
          }
          .gphr-cta h3 span {
            background: linear-gradient(135deg, #f1cf85, #b58a3f);
            -webkit-background-clip: text; background-clip: text; color: transparent;
            font-style: italic; font-weight: 600;
          }
          .gphr-cta p {
            position: relative;
            color: #9aa0a8; margin: 0 auto 24px; max-width: 540px;
            font-size: 14.5px; line-height: 1.65;
          }
          .gphr-cta-actions {
            position: relative;
            display: inline-flex; flex-wrap: wrap; gap: 12px; justify-content: center;
          }

          /* BUTTONS */
          .gphr-btn {
            display: inline-flex; align-items: center; gap: 10px;
            padding: 13px 26px;
            border-radius: 12px;
            font-size: 13px;
            font-weight: 800;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            text-decoration: none;
            cursor: pointer;
            border: none;
            transition: transform .2s ease, box-shadow .25s ease, background .2s ease, gap .2s ease;
          }
          .gphr-btn-primary {
            background: linear-gradient(135deg, #f1cf85, #d4a857 50%, #b58a3f);
            color: #0b0b0c;
          }
          .gphr-btn-primary:hover {
            transform: translateY(-2px);
            box-shadow: 0 14px 30px rgba(212,168,87,0.35);
            color: #0b0b0c;
            text-decoration: none;
            gap: 12px;
          }
          .gphr-btn-ghost {
            background: rgba(255,255,255,0.04);
            border: 1px solid rgba(212,168,87,0.3);
            color: #d4a857;
          }
          .gphr-btn-ghost:hover {
            background: rgba(212,168,87,0.12);
            border-color: rgba(212,168,87,0.55);
            color: #f1cf85;
            text-decoration: none;
            transform: translateY(-2px);
          }
          .gphr-btn-wa {
            background: linear-gradient(135deg, #25d366, #128c7e);
            color: #fff;
          }
          .gphr-btn-wa:hover {
            transform: translateY(-2px);
            box-shadow: 0 12px 26px rgba(37,211,102,0.35);
            color: #fff;
            text-decoration: none;
          }

          /* RESPONSIVE */
          @media (max-width: 991px) {
            .gphr-steps { grid-template-columns: 1fr; }
            .gphr-channels { grid-template-columns: 1fr; }
            .gphr-tier-grid { grid-template-columns: repeat(2, 1fr); }
            .gphr-split { grid-template-columns: 1fr; }
            .gphr-title { font-size: 32px; }
            .gphr-cta h3 { font-size: 24px; }
          }
          @media (max-width: 575px) {
            .gphr-section { padding: 40px 14px 70px; }
            .gphr-title { font-size: 26px; }
            .gphr-block-head h2 { font-size: 22px; }
            .gphr-step { padding: 16px; grid-template-columns: 48px 1fr; }
            .gphr-step-num { width: 48px; height: 48px; }
            .gphr-step-num span { font-size: 18px; }
            .gphr-tier-grid { grid-template-columns: 1fr; }
            .gphr-channel { padding: 14px; }
            .gphr-block-card { padding: 22px 18px; }
            .gphr-security-card { grid-template-columns: 1fr; padding: 22px 18px; }
            .gphr-cta { padding: 36px 20px; }
            .gphr-cta h3 { font-size: 22px; }
          }
        `}</style>
      </section>

      <Footer />
      <Scrollbar />
    </Fragment>
  );
};

export default HowToRedeem;
